import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useInstallPrompt, incrementNavCount } from '@hooks/useInstallPrompt';
import { awaitCelebrationIdle } from '@utils/celebrationMarker';

// Require multiple distinct navigations before prompting so we only ask
// users to install after they've shown sustained engagement with the app.
// Six was chosen from UX testing as a balance between "not too early" and
// "not so late that frequent users never see the prompt".
const NAV_THRESHOLD = 6;

/**
 * Pure side-effect component: tracks navigation count and triggers the install
 * prompt after the user has navigated enough times. Does not render any UI —
 * the drawer is owned by InstallPromptProvider.
 */
export function InstallPromptInitializer() {
  const { shouldPrompt, show } = useInstallPrompt();
  const location = useLocation();
  const prevPathnameRef = useRef(location.pathname);

  useEffect(() => {
    const navigated = location.pathname !== prevPathnameRef.current;
    if (navigated) prevPathnameRef.current = location.pathname;
    const crossed = navigated && incrementNavCount() >= NAV_THRESHOLD;
    if (!crossed || !shouldPrompt) return;
    // Wait out a celebration that is pending, queued or on screen, then take a turn on the
    // overlay mutex: the prompt lands when the current overlay closes, never on top of
    // "You're all set" (overlay-system-design §6.7 item 2). Prompts can't reach this decision
    // before CelebrationHost claims a posted intent, so this isn't about cold start; it's about
    // two celebrations back to back — without the wait, a prompt could slip in between the
    // first celebration closing and the second intent being claimed.
    const controller = new AbortController();
    void awaitCelebrationIdle(controller.signal).then(() => {
      if (controller.signal.aborted) return;
      show();
    });
    return () => {
      controller.abort();
    };
  }, [location.pathname, shouldPrompt, show]);

  return null;
}
